import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './App.jsx'

window.addEventListener('vite:preloadError', (event) => {
  console.warn('New version available, reloading page to fetch new assets...');
  window.location.reload();
});

function injectDeferredScript({ id, src, attributes = {} }) {
  if (
    typeof document === 'undefined' ||
    document.querySelector(`script[data-aikovibe-script="${id}"]`)
  ) {
    return null
  }

  const script = document.createElement('script')

  script.src = src
  script.async = true
  script.defer = true
  script.dataset.aikovibeScript = id

  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      script.setAttribute(key, String(value))
    }
  })

  document.head.appendChild(script)

  return script
}

async function initializeDeferredSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return
  }

  try {
    const Sentry = await import('@sentry/react')

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    })
  } catch (error) {
    console.error('Deferred Sentry init failed.', error)
  }
}

function initializeDeferredAnalytics() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  if (import.meta.env.VITE_UMAMI_WEBSITE_ID) {
    injectDeferredScript({
      id: 'umami',
      src: 'https://cloud.umami.is/script.js',
      attributes: {
        'data-website-id': import.meta.env.VITE_UMAMI_WEBSITE_ID,
      },
    })
  }

  if (window.fbq) {
    window.fbq('track', 'PageView')
    return
  }

  ;(function loadMetaPixel(f, b, e, v, n, t, s) {
    if (f.fbq) {
      return
    }

    n = f.fbq = function fbqProxy() {
      if (n.callMethod) {
        n.callMethod.apply(n, arguments)
        return
      }

      n.queue.push(arguments)
    }

    if (!f._fbq) {
      f._fbq = n
    }

    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = true
    t.src = v
    t.dataset.aikovibeScript = 'meta-pixel'
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')

  window.fbq('init', '1010897167999045')
  window.fbq('track', 'PageView')
}

function RootApp() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    let timeoutId = null

    const startDeferredWork = () => {
      timeoutId = window.setTimeout(() => {
        initializeDeferredAnalytics()
        void initializeDeferredSentry()
      }, 2000)
    }

    if (document.readyState === 'complete') {
      startDeferredWork()
    } else {
      window.addEventListener('load', startDeferredWork, { once: true })
    }

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }

      window.removeEventListener('load', startDeferredWork)
    }
  }, [])

  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <App />
    </BrowserRouter>
  )
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <RootApp />
  </StrictMode>,
)
